import type { ComponentType, FC } from "react"
import { lazy, Suspense, useRef, useEffect, useState } from "react"
import type { CapsuleProps } from "~/components/capsules"
import type { WithActivityTypeFragment } from "~/graphql"
import type { ReferralCapsuleData } from "./Referral"
import { getCapsuleSkeleton } from "./CapsuleSkeletons"
import { CapsuleErrorBoundary } from "./CapsuleErrorBoundary"

// Map of capsule types to their dynamic import functions
const capsuleImportMap = {
  CapsuleAccordion: () => import("./Accordion"),
  CapsuleAccordionGroup: () => import("./AccordionGroup"),
  CapsuleActivityCard: () => import("./ActivityCard"),
  CapsuleActivityType: () => import("./ActivityType"),
  CapsuleUgcCard: () => import("./UgcCard"),
  CapsuleUgcCardGroup: () => import("./UgcCardGroup"),
  CapsuleActivityCardGroup: () => import("./ActivityCardGroup"),
  CapsuleButton: () => import("./Button"),
  CapsuleCollection: () => import("./Collection"),
  CapsuleCollectionCarousel: () => import("./CollectionCarousel"),
  CapsuleCollectionPicks: () => import("./CollectionPicks"),
  CapsuleEmbed: () => import("./Embed"),
  CapsuleHero: () => import("./Hero"),
  CapsuleKlaviyoForm: () => import("./KlaviyoForm"),
  LocationInfoCard: () => import("./LocationInfoCard"),
  CapsuleMerchingRow: () => import("./Merching"),
  CapsuleModule: () => import("./Module"),
  CapsuleMultiImage: () => import("./MultiImage"),
  CapsuleProductList: () => import("./ProductList"),
  CapsuleReferral: () => import("./Referral"),
  CapsuleScript: () => import("./Script"),
  CapsuleText: () => import("./Text"),
  CapsuleVideo: () => import("./Video")
}

// Cache for lazy components to avoid recreating them
const lazyComponentCache = new Map<
  string,
  ComponentType<
    CapsuleProps & {
      capsule: any
      activityTypeMap?: Record<string, WithActivityTypeFragment>
      activityPriceMap?: Record<string, number | null>
      referralData?: ReferralCapsuleData
    }
  >
>()

// Prefetch queue for intersection observer
const prefetchQueue = new Set<string>()

// Prefetch a component before it's needed
export const prefetchCapsule = (capsuleType: string) => {
  if (
    capsuleImportMap[capsuleType as keyof typeof capsuleImportMap] &&
    !prefetchQueue.has(capsuleType)
  ) {
    prefetchQueue.add(capsuleType)
    // Trigger the dynamic import to start loading
    capsuleImportMap[capsuleType as keyof typeof capsuleImportMap]()
  }
}

// Get or create a lazy component for a capsule type
export const getCapsuleComponent = (
  capsuleType: string
): ComponentType<
  CapsuleProps & {
    capsule: any
    activityTypeMap?: Record<string, WithActivityTypeFragment>
    activityPriceMap?: Record<string, number | null>
    referralData?: ReferralCapsuleData
  }
> | null => {
  if (!capsuleImportMap[capsuleType as keyof typeof capsuleImportMap]) {
    return null
  }

  // Return cached component if it exists
  if (lazyComponentCache.has(capsuleType)) {
    return lazyComponentCache.get(capsuleType)!
  }

  // Create new lazy component
  const LazyComponent = lazy(async () => {
    const module =
      await capsuleImportMap[capsuleType as keyof typeof capsuleImportMap]()

    return { default: module.default }
  })

  lazyComponentCache.set(capsuleType, LazyComponent)
  return LazyComponent
}

// Hook to track if element is near viewport
const useIntersectionVisibility = (
  options: { rootMargin?: string; threshold?: number } = {}
) => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (typeof window === "undefined" || !ref.current) return

    // Check if IntersectionObserver is available
    if (!("IntersectionObserver" in window)) {
      // Fallback: show immediately if IntersectionObserver is not supported
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            // Once visible, we can disconnect the observer
            observer.disconnect()
          }
        })
      },
      {
        rootMargin: options.rootMargin || "200px", // Load when 200px from viewport
        threshold: options.threshold || 0
      }
    )

    observer.observe(ref.current)

    return () => {
      observer.disconnect()
    }
  }, [options.rootMargin, options.threshold])

  // Tab-driven focus jumps straight to the next focusable element without
  // scrolling past elements in between, so the observer above never fires
  // for capsules a keyboard user tabs past before scrolling. Expose a manual
  // trigger so focus reaching an unloaded capsule can force it to load.
  const markVisible = () => setIsVisible(true)

  return { ref, isVisible, markVisible }
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'

// Loading component shown while capsules are being loaded
export const CapsuleLoadingFallback = ({
  capsuleType,
  capsule
}: {
  capsuleType: string
  capsule?: any
}) => {
  const Skeleton = getCapsuleSkeleton(capsuleType)
  return <Skeleton capsule={capsule} />
}

// Mounts in the same Suspense boundary as a lazy capsule, so its effect runs
// only once the capsule's content has committed.
const OnResolved: FC<{ onResolved: () => void }> = ({ onResolved }) => {
  useEffect(onResolved, [onResolved])
  return null
}

// Wrapper component that handles the dynamic loading
export const DynamicCapsule: ComponentType<{
  capsuleType: string
  page: any
  capsule: any
  customer?: any
  membershipProduct?: any
  topBunkMembershipDiscount?: any
  processingFeeProductPrice?: number
  activityTypeMap?: Record<string, WithActivityTypeFragment>
  activityPriceMap?: Record<string, number | null>
  referralData?: ReferralCapsuleData
  index: number
}> = ({
  capsuleType,
  page,
  capsule,
  customer,
  membershipProduct,
  topBunkMembershipDiscount,
  processingFeeProductPrice,
  activityTypeMap,
  activityPriceMap,
  referralData,
  index
}) => {
  // Use intersection observer to track when this capsule becomes visible
  const {
    ref: visibilityRef,
    isVisible,
    markVisible
  } = useIntersectionVisibility({
    rootMargin: "200px" // Start loading when 200px from viewport
  })

  // Get cached lazy component when visible
  const CapsuleComponent = isVisible ? getCapsuleComponent(capsuleType) : null

  const focusPendingRef = useRef(false)

  // Trigger prefetching when this component becomes visible
  useEffect(() => {
    if (isVisible) {
      const nextCapsuleType =
        page.capsulesCollection?.items[index + 1]?.__typename
      if (nextCapsuleType) {
        prefetchCapsule(nextCapsuleType)
      }
    }
  }, [isVisible, page.capsulesCollection?.items, index])

  // If keyboard focus forced this capsule to load, hand focus to its first
  // focusable element once it renders so the user continues from there
  // instead of losing focus to whatever came next in the tab order. Runs from
  // OnResolved, which sits in the same Suspense boundary as the capsule, so it
  // fires only after the lazy content has actually mounted.
  const handleResolved = () => {
    const container = visibilityRef.current
    if (!focusPendingRef.current || !container) return
    focusPendingRef.current = false

    const target = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
    if (target) {
      target.focus()
      return
    }

    // The capsule has no focusable content of its own (e.g. plain text or a
    // heading) — continue the tab sequence to whatever comes after it instead
    // of parking focus on a non-interactive container, which would read to a
    // screen reader as if a heading or paragraph were itself focused.
    const next = [
      ...document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ].find(
      el =>
        container.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
    )
    next?.focus()
  }

  // Show skeleton until visible and component is loaded
  if (!isVisible || !CapsuleComponent) {
    return (
      <div ref={visibilityRef}>
        <div
          tabIndex={0}
          onFocus={() => {
            focusPendingRef.current = true
            markVisible()
          }}
        >
          <CapsuleLoadingFallback capsuleType={capsuleType} capsule={capsule} />
        </div>
      </div>
    )
  }

  return (
    <div ref={visibilityRef}>
      <CapsuleErrorBoundary capsuleType={capsuleType}>
        <Suspense
          fallback={
            <CapsuleLoadingFallback
              capsuleType={capsuleType}
              capsule={capsule}
            />
          }
        >
          <CapsuleComponent
            page={page}
            capsule={capsule}
            customer={customer}
            membershipProduct={membershipProduct}
            topBunkMembershipDiscount={topBunkMembershipDiscount}
            processingFeeProductPrice={processingFeeProductPrice}
            activityTypeMap={activityTypeMap}
            activityPriceMap={activityPriceMap}
            referralData={referralData}
            onResolved={handleResolved}
            index={index}
          />
          {capsuleType !== "CapsuleMultiImage" && (
            <OnResolved onResolved={handleResolved} />
          )}
        </Suspense>
      </CapsuleErrorBoundary>
    </div>
  )
}
